// src/services/businessMessagingApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { 
  OMNISTACK_BASE_URL, 
  getPrepareHeaders 
} from '@/hooks/common/common-functions'

export const businessMessagingApi = createApi({
  reducerPath: 'businessMessagingApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${OMNISTACK_BASE_URL}business-messaging`,
    prepareHeaders: (headers) => {
      // Add standard headers
      getPrepareHeaders({
        headers,
        isClientApiKey: true,
      });
      
      // Add business-specific API key
      const businessApiKey = localStorage.getItem('businessApiKey');
      if (businessApiKey) {
        headers.set('business-x-api-key', businessApiKey);
      }
      
      return headers;
    },
  }),
  tagTypes: ['Messages', 'Conversations', 'UnreadCount'],
  endpoints: (builder) => ({
    // Send text message to client
    sendMessage: builder.mutation({
      query: ({ businessId, appClientId, ...messageData }) => ({
        url: `/${businessId}/send/${appClientId}`,
        method: 'POST',
        body: messageData
      }),
       // @ts-ignore
      invalidatesTags: (result, error, { appClientId }) => [
        { type: 'Messages', id: appClientId },
        'Conversations',
        { type: 'UnreadCount', id: 'TOTAL' },
        { type: 'UnreadCount', id: appClientId }
      ]
    }),

    // Send file message to client
    sendFileMessage: builder.mutation({
      query: ({ businessId, appClientId, file, content, senderUserId }) => {
        const formData = new FormData();
        formData.append('file', file);
        if (content) formData.append('content', content);
        if (senderUserId) formData.append('senderUserId', senderUserId);

        return {
          url: `/${businessId}/send/${appClientId}/file`,
          method: 'POST',
          body: formData,
          formData: true
        };
      },
      // @ts-ignore
      invalidatesTags: (result, error, { appClientId }) => [
        { type: 'Messages', id: appClientId },
        'Conversations',
        { type: 'UnreadCount', id: 'TOTAL' },
        { type: 'UnreadCount', id: appClientId }
      ]
    }),

    // Get conversation history with a specific client
    getConversation: builder.query({
      query: ({ businessId, appClientId, page = 1, limit = 50 }) => ({
        url: `/${businessId}/conversation/${appClientId}`,
        params: { page, limit }
      }),
       // @ts-ignore
      providesTags: (result, error, { appClientId }) => [
        { type: 'Messages', id: appClientId }
      ]
    }),

    // Mark messages as read
    markMessagesAsRead: builder.mutation({
      query: ({ businessId, appClientId, messageIds }) => ({
        url: `/${businessId}/conversation/${appClientId}/mark-read`,
        method: 'PUT',
        body: messageIds ? { messageIds } : {}
      }),
       // @ts-ignore
      invalidatesTags: (result, error, { appClientId }) => [
        { type: 'Messages', id: appClientId }
      ]
    })
  })
});

export const {
  useSendMessageMutation,
  useSendFileMessageMutation,
  useGetConversationQuery,
  useMarkMessagesAsReadMutation
} = businessMessagingApi;